/* ==========================================================================
   Cart drawer — free-shipping progress animation + 100% celebration
   --------------------------------------------------------------------------
   Pairs with the free-shipping styles in custom.css, the module markup in
   snippets/aa-cart-drawer.liquid, and the shared burst in
   assets/aa-free-shipping-confetti.js.

   What it does:
   1. Animates the free-shipping progress bar from 0% up to its server-rendered
      width on open, so the gold truck badge glides to the progress point. It
      reuses the existing `transition: width .3s ease` on the fill — no extra
      timers/keyframes.
   2. Fires a one-off confetti burst when the cart crosses to the free-shipping
      threshold (100%), and hides/reveals the message via `.is-celebrating`.

   The "FREE SHIPPING!" phrase highlight is rendered directly in Liquid
   (.free-shipping-message__highlight), so it survives Section-Rendering
   re-renders natively and needs no JS.

   Why it's written this way (theme behavior):
   - On add/remove the drawer rebuilds `.drawer__inner` via the Section
     Rendering API, so anything inside is wiped. The only stable hook is
     <cart-drawer> + its `.active` class (never replaced), so we observe it and
     re-run after each cart change.
   - We never touch `#CartDrawer`'s display (Safari close hack) and key the open
     animation off the `.active` class, not visibility/display.
   ========================================================================== */

(function () {
  function init() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    // Animate the fill. introFromZero=true sweeps up from empty (on open);
    // otherwise it glides from the previous value to the new server-rendered
    // width — up OR down — but only when the fill NODE was actually replaced
    // (a cart re-render), so unrelated DOM changes never retrigger it. The theme
    // swaps the fill for a fresh node, so the CSS `transition: width` can't fire
    // on its own; we apply the old value to the new node (before paint), then
    // set the target, so the transition glides between them.
    var lastWidth = null;
    var lastFillNode = null;
    var confettiFired = false;

    function handleFill(introFromZero) {
      var fill = drawer.querySelector('.free-shipping-indicator__fill');
      if (!fill) return;
      if (!introFromZero && fill === lastFillNode) return; // no node swap → ignore
      lastFillNode = fill;
      var target = fill.style.width; // server-rendered % for this cart state
      if (!target) return;
      var visible = introFromZero || drawer.classList.contains('active');
      var start = introFromZero ? '0%' : lastWidth != null ? lastWidth : target;
      if (visible && start !== target && typeof fill.animate === 'function') {
        // Glide start -> target with the Web Animations API and DON'T touch
        // fill.style.width — it stays at the server `target`, so the bar's
        // resting value is always correct and a replaced node can't get stuck.
        // (Setting the inline width here re-triggered the CSS `transition: width`
        // rule, so the bar animated TWICE — once via that transition and once
        // via WAAPI — which was the double up/down slide.)
        fill.animate([{ width: start }, { width: target }], {
          duration: 400,
          easing: 'ease'
        });
      }
      lastWidth = target;

      // 100% CELEBRATION — fire once per fill (re-arms when it drops below 100).
      if (target !== '100%') {
        confettiFired = false;
        cancelCelebrate(); // dropping below the threshold cancels a pending burst
      } else if (!confettiFired) {
        confettiFired = true;
        if (!introFromZero && visible) celebrate();
      }
    }

    // Hide the message, burst confetti from the bar, then fade the text back in.
    var celebrateTimers = [];
    function celebrate() {
      if (
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return; // respect reduced-motion: skip the burst + text hide
      }
      if (!drawer.querySelector('.free-shipping-indicator')) return;
      drawer.classList.add('is-celebrating'); // CSS hides the message (kept in layout)
      celebrateTimers.push(setTimeout(fireConfetti, 420)); // after the bar fills to 100%
      celebrateTimers.push(
        setTimeout(function () {
          drawer.classList.remove('is-celebrating'); // the new text comes back in
        }, 1000)
      );
    }

    // If the cart drops back below the threshold before the burst fires, cancel
    // it — confetti should only ever play on the way UP, never on the way down.
    function cancelCelebrate() {
      while (celebrateTimers.length) clearTimeout(celebrateTimers.pop());
      drawer.classList.remove('is-celebrating');
    }

    // The burst itself lives in assets/aa-free-shipping-confetti.js so the Build
    // A Subscription box fires the identical animation from its own bar.
    function fireConfetti() {
      if (typeof window.AAFreeShippingConfetti !== 'function') return;
      window.AAFreeShippingConfetti(drawer.querySelector('.free-shipping-indicator'));
    }

    function onOpen() {
      handleFill(true);
    }

    // OPEN: fire only on the not-active -> active transition so unrelated class
    // changes on <cart-drawer> don't restart the intro. The class lives on
    // <cart-drawer>, which is never replaced on re-render.
    var wasActive = drawer.classList.contains('active');
    new MutationObserver(function () {
      var isActive = drawer.classList.contains('active');
      if (isActive && !wasActive) onOpen();
      wasActive = isActive;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    // RE-RENDER: a cart change replaces the drawer contents. Glide the fill to its
    // new value. Runs in the MutationObserver callback (before paint), guarded by
    // fill-node identity to act once per actual change. Width/style changes aren't
    // childList mutations, so this observer doesn't re-trigger itself (no loop).
    new MutationObserver(function () {
      handleFill(false);
    }).observe(drawer, { childList: true, subtree: true });

    // If the drawer is already open when this loads (e.g. reopened on back nav).
    if (wasActive) onOpen();
  }

  // Run after the theme's deferred scripts (pubsub/cart) and markup exist.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
